'use client';

import { XMarkIcon } from '@heroicons/react/16/solid';
import { Dialog as DialogPrimitive } from 'radix-ui';
import * as React from 'react';

import { cn } from '@/lib/cn';

const SizeMap = {
  xs: 'max-w-md w-[calc(100%-2rem)]',
  sm: 'max-w-lg w-[calc(100%-2rem)]',
  md: 'max-w-2xl w-[calc(100%-2rem)]',
  lg: 'max-w-3xl w-[calc(100%-2rem)]',
  xl: 'max-w-4xl w-[calc(100%-2rem)]',
  '2xl': 'max-w-5xl w-[calc(100%-2rem)]',
  '3xl': 'max-w-6xl w-[calc(100%-2rem)]',
};

export type DialogSize = keyof typeof SizeMap;

export type DialogProps = DialogPrimitive.DialogProps;

const DialogContext = React.createContext<{
  readonly onOpenChange: ((open: boolean) => void) | undefined;
}>({ onOpenChange: undefined });

function Dialog({ onOpenChange, ...props }: DialogPrimitive.DialogProps) {
  const value = React.useMemo(() => ({ onOpenChange }), [onOpenChange]);
  return (
    <DialogContext.Provider value={value}>
      <DialogPrimitive.Root onOpenChange={onOpenChange} {...props} />
    </DialogContext.Provider>
  );
}

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = ({
  children,
  ...props
}: DialogPrimitive.DialogPortalProps) => (
  <DialogPrimitive.Portal {...props}>
    <div className='fixed inset-0 z-50 flex items-center justify-center'>
      {children}
    </div>
  </DialogPrimitive.Portal>
);
DialogPortal.displayName = DialogPrimitive.Portal.displayName;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, onClick, ...props }, ref) => (
  <DialogPrimitive.Overlay
    data-row-click-ignore
    ref={ref}
    onClick={(event) => {
      event.stopPropagation();
      onClick?.(event);
    }}
    className={cn(
      'fixed inset-0 z-50 bg-black/50 backdrop-blur-sm data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    size?: DialogSize;
    overlayClassName?: string;
  }
>(
  (
    {
      className,
      children,
      size = 'md',
      overlayClassName,
      onClick,
      onEscapeKeyDown,
      onInteractOutside,
      ...props
    },
    ref
  ) => {
    const { onOpenChange } = React.useContext(DialogContext);

    const handleEscapeKeyDown =
      onEscapeKeyDown ??
      (onOpenChange
        ? (e: KeyboardEvent) => {
            e.stopPropagation();
            e.preventDefault();
            onOpenChange(false);
          }
        : undefined);

    const handleInteractOutside = (
      event: Parameters<NonNullable<typeof onInteractOutside>>[0]
    ) => {
      const target = event.target as Element | null;
      if (target?.closest('[data-sonner-toaster]')) {
        event.preventDefault();
        return;
      }
      onInteractOutside?.(event);
    };

    return (
      <DialogPortal>
        <DialogOverlay className={overlayClassName} />
        <DialogPrimitive.Content
          ref={ref}
          className={cn(
            'fixed z-50 grid w-full animate-in rounded-b-lg border bg-surface p-6 shadow-lg data-[state=open]:fade-in-90 data-[state=open]:slide-in-from-bottom-10 sm:rounded-lg sm:zoom-in-90 data-[state=open]:sm:slide-in-from-bottom-0',
            SizeMap[size],
            className
          )}
          onClick={(event) => {
            event.stopPropagation();
            onClick?.(event);
          }}
          onEscapeKeyDown={handleEscapeKeyDown}
          onInteractOutside={handleInteractOutside}
          {...props}
        >
          {children}
        </DialogPrimitive.Content>
      </DialogPortal>
    );
  }
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex items-center justify-between border-b p-4', className)}
    {...props}
  />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex items-center justify-end gap-2 border-t p-4',
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    {...props}
    ref={ref}
    className={cn(
      'text-lg leading-none font-semibold tracking-tight',
      className
    )}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

const DialogClose = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Close>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Close>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Close className={cn(className)} ref={ref} {...props} />
));

DialogClose.displayName = DialogPrimitive.Title.displayName;

const DialogCloseIcon = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Close>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Close>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Close
    className={cn(
      'absolute top-4 right-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground',
      className
    )}
    ref={ref}
    {...props}
  >
    <XMarkIcon className='size-4' />
    <span className='sr-only'>Close</span>
  </DialogPrimitive.Close>
));

DialogCloseIcon.displayName = DialogPrimitive.Title.displayName;

export {
  Dialog,
  DialogClose,
  DialogCloseIcon,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
